import { GenericSelect } from '@/components/GenericSelect';
import { MediaPreview } from '@/components/MediaPreview';
import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { type CreateMediaInputType, checkMediaCreateInput } from '@shelflix/schemas/media/create';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import type { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import Dropzone, { CropArea } from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMediaFormStore } from './store';

/**
 * Converts a File object to a base64 string without the data URL prefix.
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function MediaCreate() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [media, setMedia] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [crop, setCrop] = useState<CropArea | undefined>(undefined);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const thumbnailDropzoneRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { formData, resetForm } = useMediaFormStore();

  const form = useForm<CreateMediaInputType & { active: boolean }>({
    defaultValues: formData,
    mode: 'all',
  });

  const query = useQuery({
    ...trpc.brand.listForMedia.queryOptions({
      recordsPerPage: 9999,
    }),
  });

  const createMediaMutation = useMutation(trpc.media.create.mutationOptions());
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const onSubmit = async (data: CreateMediaInputType) => {
    if (createMediaMutation.isPending || uploadImageMutation.isPending) return;
    setIsSubmitting(true);

    try {
      const name = media ? media?.name : 'arquivo';
      const extension = name.split('.').pop() ?? '';
      const size = media ? media.size : 0;
      const isVideo = ['mp4', 'mov', 'webm'].includes(extension.toLowerCase());

      let duration = 0;
      if (isVideo && media) {
        const url = URL.createObjectURL(media);
        duration = await new Promise<number>((resolve) => {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.src = url;
          video.onloadedmetadata = () => {
            resolve(video.duration);
            URL.revokeObjectURL(url);
          };
        });
      }

      // First, upload the thumbnail with compression if provided
      let thumbnailKey: string | undefined;
      if (thumbnail) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'media-thumbnail',
          cropOptions: crop,
        });
        thumbnailKey = uploadResult.key;
        logger.info('Thumbnail uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      const payload = {
        ...data,
        fileType: media?.type ?? '',
        thumbnailKey, // Use pre-uploaded compressed thumbnail key
        filename: name,
        extension,
        size,
        duration: Math.floor(duration),
      };

      const response = await createMediaMutation.mutateAsync(payload);

      // Upload the main media file (video/image) - this still uses presigned URL
      if (media) {
        setIsUploading(true);
        const formData = new FormData();
        Object.entries(response.uploadUrl.fields ?? {}).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', media);
        await fetch(response.uploadUrl.url, {
          method: 'POST',
          body: formData,
        });
      }

      resetForm();
      navigate('/media', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao cadastrar mídia: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
      setIsSubmitting(false);
    }
  };

  usePreventLeave(isUploading);

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-8">
          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                {!media ? 'Carregar mídia' : 'Simulação e informações'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="fileType"
                rules={{ validate: (value) => checkMediaCreateInput('fileType', value) }}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-[3px]">
                    <FormControl>
                      {/* <Dropzone
                        removable={true}
                        onFilesSelected={(files) => {
                          setMedia(files[0]);
                          field.onChange(files[0]?.type);
                        }}
                        ref={dropzoneRef}
                        title="Arraste ou clique aqui para selecionar seu arquivo"
                        description="Extensões permitidas: MP4, GIF, JPG, PNG Proporção recomendada: 16:9 (Máx. 100mb)"
                        value={media ? [media] : undefined}
                      /> */}
                      {!media ? (
                        <Dropzone
                          removable={true}
                          onFilesSelected={(files) => {
                            setMedia(files[0]);
                            field.onChange(files[0]?.type);
                          }}
                          ref={dropzoneRef}
                          title="Arraste ou clique aqui para selecionar seu arquivo"
                          description="Extensões permitidas: MP4, GIF, JPG, PNG Proporção recomendada: 16:9 (Máx. 100mb)"
                          value={media ? [media] : undefined}
                          mimeTypes={['video/mp4', 'image/gif', 'image/jpeg', 'image/png']}
                          maxSizeInBytes={100 * 1024 * 1024}
                        />
                      ) : (
                        <MediaPreview
                          file={media}
                          onReplace={() => setMedia(null)}
                          showDeleteButton={false}
                          loading={false}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex w-full flex-col gap-4 sm:flex-row">
            <Card className="flex w-full rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                  Informações de mídia
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-[27px_12px] pt-4">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ validate: (value) => checkMediaCreateInput('name', value) }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-[3px]">
                      <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome</FormLabel>
                      <FormControl>
                        <Input className="h-10" placeholder="Digite o nome da mídia" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex w-full flex-col flex-wrap gap-4 sm:flex-row">
                  <FormField
                    control={form.control}
                    name="brand"
                    rules={{ validate: (value) => checkMediaCreateInput('brand', value) }}
                    render={({ field }) => (
                      <FormItem className="flex grow flex-col gap-[3px] md:basis-[240px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca</FormLabel>
                        <FormControl>
                          <GenericSelect<BrandDto>
                            onSelect={(brand) => field.onChange(brand.uuid ?? brand)}
                            value={field.value}
                            loading={query.isLoading}
                            dependencies={[query.data]}
                            fetchItems={async (search) => {
                              const brands = (query.data?.brands ?? []).sort((a, b) => a.name.localeCompare(b.name));
                              if (!search) return brands;
                              return brands.filter((brand) => brand.name.toLowerCase().includes(search.toLowerCase()));
                            }}
                            renderItem={(brand) => (
                              <div>
                                <div className="text-sm font-medium">{brand.name}</div>
                                <div className="text-muted-foreground text-xs">{brand.category}</div>
                              </div>
                            )}
                            getItemLabel={(brand) => brand.name}
                            getItemKey={(brand) => brand.uuid}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="displayFormat"
                    rules={{ validate: (value) => checkMediaCreateInput('displayFormat', value) }}
                    render={({ field }) => (
                      <FormItem className="flex grow flex-col gap-[3px] md:grow-0 md:basis-[240px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Formato de exibição
                        </FormLabel>
                        <FormControl>
                          <Select
                            defaultValue={field.value ?? undefined}
                            value={field.value ?? undefined}
                            onValueChange={(displayFormat: EDisplayFormat) => {
                              field.onChange(displayFormat);
                            }}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              {Object.values(EDisplayFormat).map((displayFormat) => (
                                <SelectItem key={displayFormat} value={displayFormat}>
                                  {campaignDisplayFormatMapper(displayFormat)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={() => (
                      <FormItem className="flex flex-col gap-[3px] md:basis-[240px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                        <FormControl>
                          <Select value={'Ativo'} disabled={true}>
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              <SelectItem value={'Ativo'}>Ativo</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">Thumbnail</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ maxWidth: 180 }} className="mx-auto mt-4 aspect-[9/16]">
                  <Dropzone
                    removable={true}
                    onFilesSelected={(files) => setThumbnail(files[0])}
                    ref={thumbnailDropzoneRef}
                    title="Arraste ou clique aqui para selecionar a thumbnail"
                    description="Extensões permitidas: JPG, PNG. Proporção obrigatória: 9:16 (Máx. 5mb)"
                    value={thumbnail ? [thumbnail] : undefined}
                    requiredAspectRatio="9:16"
                    className="h-full w-full"
                    cropModal={true}
                    cropValue={crop}
                    onCropChange={(crop) => setCrop(crop)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flexgrow-0 mb-10 flex w-full items-center justify-center">
            <Button
              type="submit"
              loading={createMediaMutation.isPending || isUploading || isSubmitting}
              className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
            >
              <Icon name="save" color="#fff" />
              <span className="[font-family:'Figtree'] text-sm">Salvar</span>
            </Button>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
